package com.neurobs.ccabserver.administrator.components

import com.neurobs.ccab.CCABServer
import com.neurobs.errors.Result
import com.neurobs.react.{ControlWrapperFactoryReact, Edit, FormDialog, FormProcessResult, FormProcessor, FormProcessorRequest, FormTable, FormTableRow, ModalContainer, VisiblePasswordEdit}
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.ReactElement
import slinky.core.facade.Hooks.{useEffect, useState}
import slinky.web.html.{className, div, span}

import scala.concurrent.ExecutionContext.Implicits.global

/**
 * Displays a modal dialog requesting and validating the data password for an account
 */
@react object DataPasswordDialog {
	/**
	 * @param server
	 * @param accountId Account for the data password
	 * @param userAccountId Account of the logged in user
	 * @param onFinished returns valid data password, or None if dialog cancelled
	 */
	case class Props
	(
		server: CCABServer,
		accountId: Long,
		userAccountId: Long,
		onFinished: Option[String] => Unit
	)

	val component = FunctionalComponent[Props] { props =>
		val (passwordBox, _) = useState(ControlWrapperFactoryReact.edit(initialValue = ""))
		val (requiredResult, setRequiredResult) = useState[Option[Either[String, Boolean]]](None)

		useEffect(() => {
			props.server.dataPasswordIsRequired(accountId = props.accountId)._2.map {
				case Left(error) => setRequiredResult(Some(Left(error.description)))
				case Right(required) =>
					if (!required) props.onFinished(Some("deprecated"))
					else setRequiredResult(Some(Right(true)))
			}
		}, Seq(props.accountId))

		def validate(): Result[Either[FormProcessResult, FormProcessor]] = {
			val password = passwordBox.getValue()
			if (password.isEmpty) Left("Please enter a password.")
			else {
				val request = props.server.checkDataPassword(accountId = props.accountId, dataPassword = password)
				Right(Right(FormProcessorRequest(request)))
			}
		}

		val table = FormTable(
			FormTableRow(
				"Password:", VisiblePasswordEdit(passwordBox, emWidth = 20)
			).withKey(key = "pw")
		)

		requiredResult match {
			case None => span()
			case Some(Right(false)) => span()
			case Some(result) =>
				val errorElement: Option[ReactElement] = result.left.toOption.map(e => div(className := "nfw-error", e))
				val content: ReactElement =
					errorElement match {
						case Some(err) => div(err, table)
						case None => table
					}
				ModalContainer(
					FormDialog(
						title = Some("Data Password"),
						description = Some(if (props.accountId == props.userAccountId) "Please enter your account's data password." else s"Please enter the data password for account ${props.accountId}."),
						submitButtonCaption = "Ok",
						customView = Some(content),
						process = () => validate(),
						onFormCancel = Some(() => props.onFinished(None)),
						onSuccess = _ => props.onFinished(Some(passwordBox.getValue()))
					)
				)
		}
	}
}
